import { API_BASE } from "./base";

type TokenGetter = () => string | null;
type TokenRefresher = () => Promise<string | null>;

let getToken: TokenGetter = () => null;
let refreshToken: TokenRefresher = async () => null;

export function configureAuth(getter: TokenGetter, refresher: TokenRefresher) {
  getToken = getter;
  refreshToken = refresher;
}

/**
 * Sessione pronta. Al caricamento della pagina il token d'accesso non c'è
 * ancora: lo ricostruisce il refresh del bootstrap (cookie httpOnly). Prima le
 * pagine partivano subito e la prima chiamata autenticata arrivava senza token:
 * 403 al ritorno da Stripe (R-64). Ora le chiamate aspettano il bootstrap.
 *
 * Il bootstrap si apre nel render dell'AuthProvider (prima degli effetti dei
 * figli, che girano prima di quelli del genitore) UNA volta per caricamento di
 * pagina: i render successivi (StrictMode, login, cambio di stato) non lo
 * riaprono. Riaprirlo dopo la chiusura lascerebbe ogni chiamata successiva in
 * attesa di un bootstrap che nessuno chiude più. Senza AuthProvider (pagine
 * pubbliche) non c'è niente da aspettare.
 */
let bootstrap: { promise: Promise<void>; resolve: () => void; settled: boolean } | null = null;

/** Solo per i test che montano più AuthProvider nello stesso modulo. */
export function resetAuthBootstrap(): void {
  bootstrap = null;
}

export function startAuthBootstrap(): void {
  if (bootstrap) return;
  let resolve: () => void = () => {};
  const promise = new Promise<void>((r) => { resolve = r; });
  bootstrap = { promise, resolve, settled: false };
}

export function finishAuthBootstrap(): void {
  if (bootstrap && !bootstrap.settled) {
    bootstrap.settled = true;
    bootstrap.resolve();
  }
}

export function waitForAuth(): Promise<void> {
  return bootstrap ? bootstrap.promise : Promise.resolve();
}

export interface ApiFetchOptions extends RequestInit {
  /** Non aspettare il bootstrap della sessione (le chiamate del bootstrap stesso). */
  skipAuthWait?: boolean;
}

export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions = {},
  _retried = false,
): Promise<T> {
  const { skipAuthWait, ...init } = options;
  if (!skipAuthWait && !_retried) {
    await waitForAuth();
  }
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init.headers as Record<string, string>),
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });

  if (res.status === 401 && !_retried) {
    const newToken = await refreshToken();
    if (newToken) {
      return apiFetch<T>(path, options, true);
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, messaggioErrore(body.detail, res.statusText), body.detail);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

/** Un errore di validazione così come lo restituisce FastAPI. */
type ErroreDiCampo = { loc?: (string | number)[]; msg?: string; type?: string };

/**
 * Riduce il `detail` di FastAPI a una frase leggibile.
 *
 * Quando la validazione fallisce, `detail` non è una stringa ma un **elenco di
 * oggetti**, uno per campo sbagliato. Passandolo così com'era al costruttore di
 * `Error`, JavaScript lo convertiva in testo nell'unico modo che conosce: chi
 * sbagliava la password si vedeva rispondere «[object Object]».
 *
 * Un errore che non dice cosa è andato storto è peggio di nessun errore: chi lo
 * legge riprova a caso.
 */
export function messaggioErrore(detail: unknown, ripiego = "Errore"): string {
  if (typeof detail === "string" && detail.trim()) return detail;

  if (Array.isArray(detail)) {
    const frasi = (detail as ErroreDiCampo[])
      .map((e) => (typeof e?.msg === "string" ? e.msg : ""))
      // Pydantic antepone «Value error, » ai messaggi scritti a mano: è rumore
      // per chi legge.
      .map((m) => m.replace(/^Value error,\s*/i, "").trim())
      .filter(Boolean);
    if (frasi.length) return [...new Set(frasi)].join(" · ");
  }

  if (detail && typeof detail === "object") {
    const m = (detail as { msg?: unknown; message?: unknown });
    if (typeof m.msg === "string") return m.msg;
    if (typeof m.message === "string") return m.message;
  }

  return ripiego;
}

/** Vero se fra gli errori di validazione ce n'è uno sul campo indicato. */
export function erroreSulCampo(detail: unknown, campo: string): boolean {
  if (!Array.isArray(detail)) return false;
  return (detail as ErroreDiCampo[]).some((e) =>
    Array.isArray(e?.loc) && e.loc.some((p) => p === campo),
  );
}

export class ApiError extends Error {
  status: number;
  /** Il `detail` originale: serve a capire QUALE campo è stato rifiutato. */
  detail: unknown;

  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }

  /** Codice leggibile dalla macchina (`detail.code`), se il backend lo manda. */
  get code(): string | undefined {
    const d = this.detail as { code?: unknown } | null | undefined;
    if (d && typeof d === "object" && typeof d.code === "string") return d.code;
    return undefined;
  }
}
